import { ChevronDown, ChevronUp, HardDrive } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { formatBaht, previewStorageUpgrade, PRO_PLAN, STORAGE_ADDON_OFFERS, type StorageAddonId, type StorageBillingPreview } from '../../shared/storagePlans';
import { formatStorage, type CloudStorageUsage } from '../lib/storage';
import { withAbortTimeout } from '../lib/async';
import { supabase } from '../lib/supabase';
import { StoragePurchasePanel } from './StoragePurchasePanel';

export function StorageUpgradePreview({ usage, userId, onPurchased }: { usage: CloudStorageUsage; userId: string; onPurchased: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<StorageAddonId>('extra_5gb');
  const id = useId();
  const [billing, setBilling] = useState<StorageBillingPreview | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteMessage, setQuoteMessage] = useState('');
  const [quoteRevision, setQuoteRevision] = useState(0);
  const [purchaseLocked, setPurchaseLocked] = useState(false);
  const [purchasesEnabled, setPurchasesEnabled] = useState(false);
  const preview = previewStorageUpgrade(selected, usage);
  useEffect(() => {
    if (!expanded) return;
    const controller = new AbortController();
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    setBilling(null);
    setQuoteMessage('');
    setQuoteLoading(true);
    const requestedAt = performance.now();
    void withAbortTimeout(async (signal) => {
      const session = (await supabase?.auth.getSession())?.data.session;
      if (!session || session.user.id !== userId) throw new Error('session_expired');
      const response = await fetch('/api/billing/storage-preview', { headers: { authorization: `Bearer ${session.access_token}` }, signal });
      if (!response.ok) throw new Error('preview_unavailable');
      return await response.json() as StorageBillingPreview;
    }, 15_000, controller.signal).then((value) => {
      if (controller.signal.aborted) return;
      if (value.available) {
        if (!value.quotes?.length || !Number.isSafeInteger(value.validUntil)) throw new Error('invalid_preview');
        const validFor = value.validUntil - value.quotes[0].calculatedAt;
        if (!Number.isSafeInteger(validFor) || validFor <= 0 || validFor > 300) throw new Error('invalid_preview');
        const remainingMs = validFor * 1000 - (performance.now() - requestedAt);
        if (remainingMs <= 0) throw new Error('expired_preview');
        expiryTimer = setTimeout(() => {
          setBilling(null);
          setQuoteMessage('ประมาณการหมดอายุแล้ว กรุณาตรวจยอดใหม่');
        }, remainingMs);
      }
      setBilling(value);
      setPurchasesEnabled(Boolean(value.purchasesEnabled));
    }).catch(() => {
      if (!controller.signal.aborted) setQuoteMessage('ยังตรวจสอบยอดวันนี้ไม่ได้ กรุณาลองใหม่');
    }).finally(() => { if (!controller.signal.aborted) setQuoteLoading(false); });
    return () => { controller.abort(); if (expiryTimer) clearTimeout(expiryTimer); };
  }, [expanded, userId, quoteRevision]);
  useEffect(() => { if (!purchasesEnabled) setPurchaseLocked(false); }, [purchasesEnabled]);
  const quote = billing?.available ? billing.quotes.find((entry) => entry.offerId === selected) : null;
  const renewalDate = quote ? new Date(quote.periodEnd * 1000).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Bangkok' }) : '';
  if (!preview) return null;

  return <div className="storage-upgrade">
    <button type="button" className="storage-upgrade-toggle" aria-expanded={expanded} aria-controls={id} disabled={purchaseLocked} onClick={() => setExpanded((value) => !value)}>
      <HardDrive size={18} /><span>ดูแพ็กเพิ่มพื้นที่</span><small>ตัวอย่างราคา</small>{expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </button>
    {expanded && <div id={id} className="storage-upgrade-content">
      <p className="storage-upgrade-intro">เพิ่มจาก Pro {formatStorage(PRO_PLAN.baseStorageBytes)} เลือกได้ครั้งละหนึ่งแพ็ก ใช้ร่วมกันทุกเครื่องในบัญชี</p>
      <fieldset className="storage-addon-options" disabled={purchaseLocked}>
        <legend>เลือกพื้นที่เสริมเพื่อดูประมาณการ</legend>
        {STORAGE_ADDON_OFFERS.map((offer) => <label key={offer.id} className={`storage-addon-option ${selected === offer.id ? 'selected' : ''}`}>
          <input type="radio" name={`${id}-offer`} value={offer.id} checked={selected === offer.id} onChange={() => setSelected(offer.id)} />
          <span>เพิ่ม {formatStorage(offer.extraBytes)}</span>
          <strong>{formatBaht(offer.amount)}<small> / {PRO_PLAN.days} วัน</small></strong>
          <small>พื้นที่รวม {formatStorage(PRO_PLAN.baseStorageBytes + offer.extraBytes)}</small>
        </label>)}
      </fieldset>
      <div className="storage-upgrade-summary" role="status" aria-live="polite" aria-atomic="true">
        <dl>
          <div><dt>พื้นที่รวมเมื่อเพิ่มแพ็ก</dt><dd>{formatStorage(preview.totalBytes)}</dd></div>
          <div><dt>คงเหลือโดยประมาณ</dt><dd>{formatStorage(preview.remainingBytes)}</dd></div>
          <div><dt>Pro + พื้นที่เสริม ต่อ {preview.periodDays} วัน</dt><dd>{formatBaht(preview.recurringAmount)}</dd></div>
        </dl>
        <p>{formatBaht(PRO_PLAN.amount)} ค่า Pro + {formatBaht(preview.addonAmount)} ค่าพื้นที่เสริม</p>
        {quote && billing?.available && <div className="storage-proration">
          <dl>
            <div><dt>ค่าพื้นที่เสริมวันนี้โดยประมาณ</dt><dd>{formatBaht(quote.amountToday)}</dd></div>
            <div><dt>ใช้ได้ถึงวันสิ้นสุดรอบ Pro</dt><dd>{renewalDate}</dd></div>
            <div><dt>{billing.renewalMode === 'automatic' ? 'รอบหน้า Pro + พื้นที่เสริม' : 'หากต่อ Pro + พื้นที่เสริมรอบหน้า'}</dt><dd>{formatBaht(quote.combinedRenewalAmount)}</dd></div>
          </dl>
          <p>คิดเฉพาะเวลาที่เหลือในรอบ Pro เดิม ไม่เลื่อนวันต่ออายุ ยอดนี้เป็นประมาณการก่อนภาษีหรือส่วนลด</p>
          {billing.renewalMode === 'manual' && <p>บัญชีนี้ต่ออายุเอง ไม่มีการตัดเงินอัตโนมัติ</p>}
          {billing.renewalMode === 'ending' && <p>Pro ตั้งยกเลิกเมื่อจบรอบแล้ว ไม่มีการตัดเงินรอบหน้า</p>}
        </div>}
        {quoteLoading && <p role="status">กำลังตรวจสอบรอบ Pro และคำนวณยอดวันนี้…</p>}
        {billing && !billing.available && <p>{billing.reason === 'trial'
          ? 'บัญชียังอยู่ช่วงทดลอง จะแสดงยอดเพิ่มพื้นที่เมื่อมีรอบ Pro ที่ชำระแล้ว'
          : billing.reason === 'payment_pending' ? 'กรุณาจัดการการชำระเงิน Pro ให้เรียบร้อยก่อนคำนวณพื้นที่เพิ่ม'
          : 'ยังยืนยันรอบชำระเงิน Pro ของบัญชีนี้ไม่ได้ จึงยังไม่แสดงยอดจ่ายวันนี้'}</p>}
        {quoteMessage && <p>{quoteMessage}</p>}
        {!quoteLoading && <button type="button" className="btn ghost small storage-quote-refresh" onClick={() => setQuoteRevision((value) => value + 1)}>ตรวจยอดวันนี้ใหม่</button>}
        {preview.overQuotaBytes > 0 && <p className="storage-upgrade-overage">ข้อมูลปัจจุบันยังเกินแพ็กนี้ {formatStorage(preview.overQuotaBytes)} กรุณาเลือกแพ็กที่ใหญ่ขึ้น</p>}
      </div>
      {purchasesEnabled && <StoragePurchasePanel userId={userId} selected={selected}
        canQuote={Boolean(billing?.available && billing.renewalMode === 'automatic')} onOfferChange={setSelected} onLockChange={setPurchaseLocked} onPurchased={onPurchased} />}
      {!purchasesEnabled && <p className="storage-upgrade-preview-note">ตัวอย่างแพ็กเสริม ยังไม่เปิดซื้อ การเลือกนี้ไม่เพิ่มพื้นที่หรือเรียกเก็บเงิน พื้นที่ที่ใช้ได้จริงดูจากตัวเลขด้านบน</p>}
    </div>}
  </div>;
}
